// components/artifacts/charts/PieChartComponent.tsx
import React from "react";
import { PieChart, Pie, Cell } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "@/components/ui/chart";
import { GraphData } from "@/types/Artifact";
import { formatNumber } from "@/lib/utils";

interface PieChartComponentProps {
  data: GraphData;
  config: Record<string, any>;
  colors: string[];
  id?: string;
}

// Custom tooltip formatter that includes units
const formatTooltipValue = (value: any, unit?: string) => {
    let y = value
    //if value is number, convert to string
    if (typeof value === 'number') {
      y = formatNumber(y);
    } 
  
    if (!unit) return value;
    
    // Handle different unit types
    switch (unit) {
      case '$':
      case '€':
      case '£':
      case '¥':
        return `${unit}${y}`;
      case '%':
        return `${y}${unit}`;
      default:
        return `${y} ${unit}`;
    }
};

export const PieChartComponent: React.FC<PieChartComponentProps> = React.memo(({
  data,
  config,
  colors,
  id,
}) => {
  const isDonut = data.type === "donut";
  const innerRadius = isDonut ? (data.innerRadius || 60) : 0;
  const outerRadius = data.outerRadius || 140;
  const valueKey = data.valueKey || "value";
  const categoryKey = data.categoryKey || "category";
  const { valueUnit } = data;

  return (
    <div className="p-4" id={id}>
      {/* Display unit information if available */}
      {/* {valueUnit && (
        <div className="mb-2 text-sm text-muted-foreground">
          Values: {valueUnit}
        </div>
      )} */}
      
      <ChartContainer
        config={config}
        className="max-h-[calc(100vh-10rem)] min-h-[400px] w-full"
      >
        <PieChart accessibilityLayer>
          <Pie
            data={data.data}
            cx="50%"
            cy="50%"
            labelLine={true}
            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
            outerRadius={outerRadius}
            innerRadius={innerRadius}
            fill="#8884d8"
            dataKey={valueKey}
            nameKey={categoryKey}
          >
            {data.data.map((_, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Pie>
          <ChartTooltip 
            content={<ChartTooltipContent 
              valueFormatter={(value) => formatTooltipValue(value, valueUnit)}
            />} 
          />
          <ChartLegend content={<ChartLegendContent />} />
        </PieChart>
      </ChartContainer>
    </div>
  );
});